// TODO remove duplicates in nodes.ts
import { z } from 'zod';

const nodeKinds = [
    'slice',
    'number',
    'strCast',
    'numCast',
    'text',
    'boolCast',
    'booleanComparison',
    'mqttPublish',
    'appletEventEmit',
    'appletEventListen',
    'varRead',
    'udpRead',
    'varWrite',
    'varWriteMultiple',
    'varAssigned',
    'script',
    'jsonAgg',
    'jsonParse',
    'numClamp',
    'txtFormat',
    'defaultVal',
    'eventSwitch',
    'timer',
    'materialAssembly',
    'materialDisassembly',
    'materialComparison',
    'materialLookup',
    'sparkplugCommander',
    'sparkplugConsumer',
    'udpWrite',
    'udpSocket',
    'start',
    'createMaterial',
    'eventDelay',
    'mqttSubscribe',
    'eventThrottle',
    'eventOneShot',
    'comparisonString',
    'comparisonNumber',
    'attributeMaterialLink',
    'performanceDataCollect',
    'calendar',
    'workOrder',
    'workInstruction',
    'processCollect',
    'workOrderSetStatus',
    'updateMaterialStatus',
    'nextTab',
    'goToTab',
    'previousTab',
    'functionalSelect',
    'functionalInputs',
    'functionalOutputs',
    'fileRead',
    'fileWrite',
    'boolean',
    'not',
    'timestamp',
] as const;

const nodeKindSchema = z.string().refine(
    (val): val is typeof nodeKinds[number] => nodeKinds.includes(val as typeof nodeKinds[number]),
    val => ({ message: `Unrecognized node kind: ${val}` })
) as z.ZodType<typeof nodeKinds[number]>;

export type NodeKind = z.infer<typeof nodeKindSchema>;

export const baseNodeCfgSchema = z.object({
    controlValues: z.record(z.unknown()).optional(),
    id: z.string(),
    kind: nodeKindSchema,
    label: z.string().optional(),
    disabled: z.boolean().default(false).optional(),
    /** provides a constant default value for a socket if there is no connection */
    socketDefaults: z.object({
        inputs: z.record(z.unknown()).optional(),
        outputs: z.record(z.unknown()).optional(),
    }).optional(),
    width: z.number().optional(),
    x: z.number().optional(),
    y: z.number().optional(),
});
export interface BaseNodeCfg extends z.infer<typeof baseNodeCfgSchema> {}

// export type NodeCfg<KIND extends NodeKind> = z.infer<typeof nodeCfgSchemaMap[KIND]>;

export type NodeDataSocketKind = 'string' | 'number' | 'varValue' | 'boolean' | 'workOrderMaterialData' | 'anyData';
export type NodeSocketKind = 'event' | NodeDataSocketKind;

type SocketCategory = 'control' | 'data';
export const socketCategoryMap = {
    anyData: 'data',
    boolean: 'data',
    event: 'control',
    number: 'data',
    string: 'data',
    varValue: 'data',
    workOrderMaterialData: 'data',
} satisfies {
    [kind in NodeSocketKind]: SocketCategory;
};
export type SocketCategoryMap = typeof socketCategoryMap;

export type ControlSocketKind = {
    [K in NodeSocketKind]: SocketCategoryMap[K] extends 'control' ? K : never;
}[NodeSocketKind];

export type DataSocketKind = {
    [K in NodeSocketKind]: SocketCategoryMap[K] extends 'data' ? K : never;
}[NodeSocketKind];

export type NodeSocketDef<CFG extends BaseNodeCfg, ALLOWED_SOCKET_KINDS extends NodeSocketKind> = {
    inputs: (cfg: CFG) => Partial<Record<string, ALLOWED_SOCKET_KINDS>>;
    outputs: (cfg: CFG) => Partial<Record<string, ALLOWED_SOCKET_KINDS>>;
};

export interface NodeSocketCfg<CFG extends BaseNodeCfg> {
    controlSockets: NodeSocketDef<CFG, ControlSocketKind>;
    dataSockets: NodeSocketDef<CFG, DataSocketKind>;
}
